import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Scale,
  Lock,
  X,
  AlertTriangle,
  Info,
  AlertCircle,
  CheckCircle2,
  Filter,
  FileText,
} from "lucide-react";
import { reconciliationsApi, bankAccountsApi, PaginatedResponse } from "../../services/api";
import SearchableSelect, { SelectOption } from "../ui/SearchableSelect";
import PremiumModal from "../ui/PremiumModal";

interface Reconciliation {
  _id: string;
  bankAccountId: { _id: string; name: string; kind: string; accountLast4: string } | string;
  periodEnd: string;
  preparedBy: { _id: string; name: string; email: string } | string;
  preparedAt: string;
  status: "draft" | "final";
  bankBalanceCents: number;
  bookBalanceCents: number;
  subledgerTotalCents: number;
  exceptions: { type: string; severity: string; note: string | null }[];
  finalizedAt: string | null;
}

const severityConfig: Record<string, { label: string; bg: string; text: string; Icon: React.FC<any> }> = {
  info: { label: "Info", bg: "bg-blue-100", text: "text-blue-700", Icon: Info },
  warn: { label: "Warning", bg: "bg-amber-100", text: "text-amber-700", Icon: AlertTriangle },
  blocker: { label: "Blocker", bg: "bg-red-100", text: "text-red-700", Icon: AlertCircle },
};

function formatCents(cents: number): string {
  return new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(cents / 100);
}

function formatDate(iso: string | null): string {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });
}

const ReconciliationPage: React.FC = () => {
  const [reconciliations, setReconciliations] = useState<Reconciliation[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [bankAccounts, setBankAccounts] = useState<any[]>([]);
  const [bankAccountFilter, setBankAccountFilter] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedReconciliation, setSelectedReconciliation] = useState<Reconciliation | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Form
  const [form, setForm] = useState({
    bankAccountId: "",
    periodEnd: "",
    bankBalanceCents: "",
    bookBalanceCents: "",
    subledgerTotalCents: "",
  });
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [saveError, setSaveError] = useState("");

  // Load bank accounts
  useEffect(() => {
    bankAccountsApi.list({ status: "open" }).then((res) => setBankAccounts(res.data?.data || []));
  }, []);

  const fetchReconciliations = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page: pagination.page, limit: pagination.limit };
      if (bankAccountFilter) params.bankAccountId = bankAccountFilter;

      const res = await reconciliationsApi.list(params);
      const data = res.data as PaginatedResponse<Reconciliation>;
      setReconciliations(data.data || []);
      setPagination((prev) => ({ ...prev, ...data.pagination }));
    } catch (err) {
      console.error("Failed to load reconciliations:", err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, bankAccountFilter]);

  useEffect(() => {
    fetchReconciliations();
  }, [fetchReconciliations]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSaveError("");
    try {
      const bankBalanceCents = Math.round(parseFloat(form.bankBalanceCents) * 100);
      const bookBalanceCents = Math.round(parseFloat(form.bookBalanceCents) * 100);
      const subledgerTotalCents = Math.round(parseFloat(form.subledgerTotalCents) * 100);

      await reconciliationsApi.create({
        bankAccountId: form.bankAccountId,
        periodEnd: form.periodEnd,
        bankBalanceCents,
        bookBalanceCents,
        subledgerTotalCents,
      });

      setShowCreateModal(false);
      setForm({ bankAccountId: "", periodEnd: "", bankBalanceCents: "", bookBalanceCents: "", subledgerTotalCents: "" });
      fetchReconciliations();
    } catch (err: any) {
      setSaveError(err.response?.data?.error || "Failed to create reconciliation");
    } finally {
      setSaving(false);
    }
  };

  const handleFinalize = async (id: string) => {
    if (!window.confirm("Finalize this reconciliation? It will be locked and cannot be edited.")) return;
    setActionLoading(id);
    try {
      await reconciliationsApi.finalize(id);
      fetchReconciliations();
      if (selectedReconciliation?._id === id) {
        const res = await reconciliationsApi.get(id);
        setSelectedReconciliation(res.data);
      }
    } catch (err: any) {
      setErrorMessage(err.response?.data?.error || "Failed to finalize");
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F4F6F8" }}>
      <div className="max-w-[1400px] mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Bank Reconciliation</h1>
            <p className="text-sm text-slate-500 mt-1">
              Reconcile bank statements against book and subledger balances
            </p>
          </div>
          <button
            onClick={() => { setSaveError(""); setShowCreateModal(true); }}
            className="inline-flex items-center gap-2 px-5 py-3 text-white font-bold rounded-xl transition-colors"
            style={{ backgroundColor: "var(--color-primary)" }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
          >
            <Plus className="h-5 w-5" />
            New Reconciliation
          </button>
        </div>

        {/* Error Banner */}
        {errorMessage && (
          <div className="mb-4 flex items-center gap-2.5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span className="flex-1">{errorMessage}</span>
            <button onClick={() => setErrorMessage(null)} className="ml-auto text-red-400 hover:text-red-600 transition-colors">&times;</button>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
          <div className="min-w-[280px]">
            <SearchableSelect
              options={bankAccounts.map((ba: any) => ({
                value: ba._id,
                label: `${ba.name} (${ba.kind})`,
              }))}
              value={bankAccountFilter || null}
              onChange={(val) => { setBankAccountFilter(val || ""); setPagination((prev) => ({ ...prev, page: 1 })); }}
              placeholder="All Bank Accounts"
              icon={Filter}
              clearable
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Reconciliation List */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
                </div>
              ) : reconciliations.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-slate-500">
                  <Scale className="h-12 w-12 mb-3 text-slate-300" />
                  <p className="font-medium text-lg">No reconciliations</p>
                  <p className="text-sm mt-1">Create your first reconciliation</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200" style={{ backgroundColor: "var(--color-primary)" }}>
                          <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">Period End</th>
                          <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden md:table-cell">Account</th>
                          <th className="text-right px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">Bank</th>
                          <th className="text-right px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden md:table-cell">Book</th>
                          <th className="text-center px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden lg:table-cell">Exceptions</th>
                          <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">Status</th>
                          <th className="text-right px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reconciliations.map((rec, i) => {
                          const accountName = typeof rec.bankAccountId === "object" && rec.bankAccountId ? rec.bankAccountId.name : "Unknown";
                          const exceptionCount = rec.exceptions?.length || 0;
                          const hasBlockers = (rec.exceptions || []).some((e) => e.severity === "blocker");

                          return (
                            <motion.tr
                              key={rec._id}
                              initial={{ opacity: 0, y: 8 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: i * 0.02 }}
                              className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors ${
                                selectedReconciliation?._id === rec._id ? "bg-emerald-50" : ""
                              }`}
                              onClick={() => setSelectedReconciliation(rec)}
                            >
                              <td className="px-6 py-4 text-sm font-medium text-slate-800">{formatDate(rec.periodEnd)}</td>
                              <td className="px-6 py-4 text-sm text-slate-600 hidden md:table-cell">{accountName}</td>
                              <td className="px-6 py-4 text-right text-sm font-semibold text-slate-800">{formatCents(rec.bankBalanceCents)}</td>
                              <td className="px-6 py-4 text-right text-sm text-slate-600 hidden md:table-cell">{formatCents(rec.bookBalanceCents)}</td>
                              <td className="px-6 py-4 text-center hidden lg:table-cell">
                                {exceptionCount > 0 ? (
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${hasBlockers ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                                    {exceptionCount}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                                    <CheckCircle2 className="h-3 w-3" /> Clean
                                  </span>
                                )}
                              </td>
                              <td className="px-6 py-4">
                                {rec.status === "final" ? (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                                    <Lock className="h-3 w-3" /> Final
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                    Draft
                                  </span>
                                )}
                              </td>
                              <td className="px-6 py-4 text-right">
                                {rec.status === "draft" && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleFinalize(rec._id); }}
                                    disabled={actionLoading === rec._id}
                                    className="text-xs font-medium px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                                  >
                                    {actionLoading === rec._id ? "..." : "Finalize"}
                                  </button>
                                )}
                              </td>
                            </motion.tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {pagination.pages > 1 && (
                    <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
                      <p className="text-sm text-slate-500">Page {pagination.page} of {pagination.pages}</p>
                      <div className="flex gap-2">
                        <button disabled={pagination.page <= 1} onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))} className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"><ChevronLeft className="h-4 w-4" /></button>
                        <button disabled={pagination.page >= pagination.pages} onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))} className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"><ChevronRight className="h-4 w-4" /></button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Detail Panel */}
          <div className="lg:col-span-1">
            {selectedReconciliation ? (
              <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5 sticky top-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">
                  Reconciliation Detail
                </h3>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Period End</span>
                    <span className="font-medium text-slate-800">{formatDate(selectedReconciliation.periodEnd)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Status</span>
                    <span className={`font-medium ${selectedReconciliation.status === "final" ? "text-emerald-700" : "text-amber-700"}`}>
                      {selectedReconciliation.status === "final" ? "Finalized" : "Draft"}
                    </span>
                  </div>
                  <div className="border-t border-slate-100 pt-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Bank Balance</span>
                      <span className="font-semibold text-slate-800">{formatCents(selectedReconciliation.bankBalanceCents)}</span>
                    </div>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Book Balance</span>
                    <span className="font-semibold text-slate-800">{formatCents(selectedReconciliation.bookBalanceCents)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Subledger Total</span>
                    <span className="font-semibold text-slate-800">{formatCents(selectedReconciliation.subledgerTotalCents)}</span>
                  </div>
                  <div className="border-t border-slate-100 pt-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Difference (Bank - Book)</span>
                      <span className={`font-bold ${selectedReconciliation.bankBalanceCents === selectedReconciliation.bookBalanceCents ? "text-emerald-700" : "text-red-600"}`}>
                        {formatCents(selectedReconciliation.bankBalanceCents - selectedReconciliation.bookBalanceCents)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Exceptions */}
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">
                    Exceptions ({selectedReconciliation.exceptions?.length || 0})
                  </h4>
                  {(selectedReconciliation.exceptions || []).length === 0 ? (
                    <p className="text-sm text-emerald-600 flex items-center gap-1">
                      <CheckCircle2 className="h-4 w-4" /> No exceptions
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {selectedReconciliation.exceptions.map((exc, i) => {
                        const config = severityConfig[exc.severity] || severityConfig.warn;
                        const ExcIcon = config.Icon;
                        return (
                          <div key={i} className={`flex items-start gap-2 p-2.5 rounded-lg ${config.bg}`}>
                            <ExcIcon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${config.text}`} />
                            <div>
                              <span className={`text-xs font-semibold ${config.text}`}>
                                {config.label}: {exc.type.replace(/_/g, " ")}
                              </span>
                              {exc.note && <p className="text-xs text-slate-600 mt-0.5">{exc.note}</p>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {selectedReconciliation.status === "draft" && (
                  <button
                    onClick={() => handleFinalize(selectedReconciliation._id)}
                    disabled={actionLoading === selectedReconciliation._id}
                    className="w-full mt-4 px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50"
                    style={{ backgroundColor: "var(--color-primary)" }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
                  >
                    {actionLoading === selectedReconciliation._id ? "Finalizing..." : "Finalize Reconciliation"}
                  </button>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8 text-center">
                <FileText className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                <p className="text-sm text-slate-500">Select a reconciliation to view details</p>
              </div>
            )}
          </div>
        </div>

        {/* Create Modal */}
        <PremiumModal
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          title="New Reconciliation"
          icon={Scale}
          size="md"
          footer={
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setShowCreateModal(false)} className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
              <button
                type="submit" form="reconciliationForm" disabled={saving || !form.bankAccountId || !form.periodEnd}
                className="px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50"
                style={{ backgroundColor: "var(--color-primary)" }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
              >
                {saving ? "Creating..." : "Create Reconciliation"}
              </button>
            </div>
          }
        >
          {saveError && <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{saveError}</div>}

          <form id="reconciliationForm" onSubmit={handleCreate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Bank Account *</label>
              <SearchableSelect
                options={bankAccounts.map((ba: any) => ({
                  value: ba._id,
                  label: `${ba.name} (${ba.kind})`,
                }))}
                value={form.bankAccountId || null}
                onChange={(val) => setForm({ ...form, bankAccountId: val || "" })}
                placeholder="Select account..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Period End Date *</label>
              <input type="date" required value={form.periodEnd} onChange={(e) => setForm({ ...form, periodEnd: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600" />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Bank Statement Balance (CAD) *</label>
              <input type="number" step="0.01" required value={form.bankBalanceCents} onChange={(e) => setForm({ ...form, bankBalanceCents: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600" placeholder="0.00" />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Book Balance (CAD) *</label>
              <input type="number" step="0.01" required value={form.bookBalanceCents} onChange={(e) => setForm({ ...form, bookBalanceCents: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600" placeholder="0.00" />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Subledger Total (CAD) *</label>
              <input type="number" step="0.01" required value={form.subledgerTotalCents} onChange={(e) => setForm({ ...form, subledgerTotalCents: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600" placeholder="0.00" />
            </div>
          </form>
        </PremiumModal>
      </div>
    </div>
  );
};

export default ReconciliationPage;
